import { useId, useState } from 'react';
import { Action, Subject } from '@erp/authz';
import type { WorkflowVersionSummary } from '@erp/rpc';
import { splitChangelog } from '@erp/workflows';
import { CircleNotchIcon, GitDiffIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { useCan } from '@/hooks/useCan';
import { useVersionJsonDiff } from '@/api/rpc/hooks/workflows/useWorkflowVersioning';
import { WorkflowJsonDiffView } from './WorkflowJsonDiffView';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function WorkflowJsonDiffButton({
  version,
  versions,
}: {
  version: WorkflowVersionSummary;
  versions: WorkflowVersionSummary[];
}) {
  const [allowed] = useCan(Action.Read, Subject.WorkflowJsonDiff);
  const [open, setOpen] = useState(false);
  if (!allowed) return null;
  const candidates = versions.filter((other) => other.id !== version.id);
  const base =
    candidates.find((other) => other.id === version.parentVersionId) ??
    candidates.toSorted((a, b) => a.version - b.version)[0];
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        disabled={!base}
        onClick={() => setOpen(true)}
      >
        <GitDiffIcon className="mr-1 h-3 w-3" />
        <T context="staff-only workflow version comparison action">JSON Diff</T>
      </Button>
      {open && base && (
        <WorkflowJsonDiffDialog
          version={version}
          versions={candidates}
          initialBaseId={base.id}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function WorkflowJsonDiffDialog({
  version,
  versions,
  initialBaseId,
  onClose,
}: {
  version: WorkflowVersionSummary;
  versions: WorkflowVersionSummary[];
  initialBaseId: string;
  onClose: () => void;
}) {
  const selectId = useId();
  const [baseId, setBaseId] = useState(initialBaseId);
  const { data, isLoading, isError, refetch } = useVersionJsonDiff({
    fromVersionId: baseId,
    toVersionId: version.id,
  });
  const base = versions.find((other) => other.id === baseId);
  const before = data ? JSON.stringify(data.from, null, 2) : '';
  const after = data ? JSON.stringify(data.to, null, 2) : '';
  return (
    <Dialog open onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>
            <T context="staff workflow JSON comparison title">
              JSON diff for v<Var>{version.version}</Var>
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="workflow JSON diff description">
              Compare the saved workflow definition with its parent or another
              version.
            </T>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Label htmlFor={selectId}>
            <T context="workflow JSON diff baseline selector label">
              Compare Against
            </T>
          </Label>
          <Select value={baseId} onValueChange={setBaseId}>
            <SelectTrigger id={selectId} className="w-80">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {versions
                .toSorted((a, b) => b.version - a.version)
                .map((other) => (
                  <SelectItem key={other.id} value={other.id}>
                    <T context="workflow JSON diff version option">
                      v<Var>{other.version}</Var> —{' '}
                      <Var>{splitChangelog(other.changelog ?? '').subject}</Var>
                    </T>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        {isLoading ? (
          <div role="status" className="flex items-center gap-2 py-8 text-sm">
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
            <T context="workflow JSON diff loading state">Loading JSON diff…</T>
          </div>
        ) : isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>
              <T context="workflow JSON diff read failure">
                Could not load this JSON diff.
              </T>
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              <T context="retry workflow JSON diff read">Retry</T>
            </Button>
          </div>
        ) : data ? (
          <div className="space-y-2">
            {before === after && (
              <p className="text-sm text-muted-foreground">
                <T context="identical workflow JSON versions">
                  No JSON changes.
                </T>
              </p>
            )}
            <WorkflowJsonDiffView
              before={before}
              after={after}
              baseVersion={base?.version}
              targetVersion={version.version}
            />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
